/**
 * useClienteData - Hook for managing cliente list and selection
 *
 * Handles: Cliente list fetching, filtering, selection, and CRUD operations
 */
import { useState, useEffect, useCallback } from 'react';
import clienteService, { Cliente } from '../services/clienteService';
import { msg, MESSAGES } from '../utils/messages';
import { sortByAlpha } from '../utils/sortAlpha';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.55

export interface UseClienteDataReturn {
  clientes: Cliente[];
  filteredClientes: Cliente[];
  selectedCliente: Cliente | null;
  setSelectedCliente: (cliente: Cliente | null) => void;
  loading: boolean;
  error: string;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showInactive: boolean;
  setShowInactive: (show: boolean) => void;
  loadClientes: () => Promise<void>;
  refreshSelectedCliente: () => Promise<void>;
}

export const useClienteData = (): UseClienteDataReturn => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Load clientes
  const loadClientes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await clienteService.listClientes();
      // Story 2.51: ordenar alfabético PT-BR por nome_fantasia (fallback razao_social)
      // Centraliza a ordenação aqui — todos os comboboxes que consomem este hook
      // automaticamente ficam ordenados (15+ componentes downstream).
      setClientes(sortByAlpha(data, ['nome_fantasia', 'razao_social']));
    } catch (err: any) {
      setError('Erro ao carregar clientes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filtered = [...clientes];

    // Filtro de ativos/inativos
    if (!showInactive) {
      filtered = filtered.filter(c => c.ativo);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.codigo.toLowerCase().includes(search) ||
          c.razao_social.toLowerCase().includes(search) ||
          c.nome_fantasia?.toLowerCase().includes(search) ||
          // Story 2.55: busca por CNPJ normalizada (ignora ./- /)
          cnpjMatch(c.cnpj, searchTerm)
      );
    }

    setFilteredClientes(filtered);
  }, [clientes, searchTerm, showInactive]);

  // Refresh selected cliente after edit
  const refreshSelectedCliente = useCallback(async () => {
    if (!selectedCliente) return;
    try {
      const updatedCliente = await clienteService.getCliente(selectedCliente.id);
      setSelectedCliente(updatedCliente);
    } catch (error) {
      console.error('Failed to refresh selected client:', error);
    }
  }, [selectedCliente]);

  // Initial load
  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  // Filter when clientes or searchTerm changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Auto-select first cliente when list loads
  useEffect(() => {
    if (filteredClientes.length > 0 && !selectedCliente) {
      setSelectedCliente(filteredClientes[0]);
    }
  }, [filteredClientes, selectedCliente]);

  return {
    clientes,
    filteredClientes,
    selectedCliente,
    setSelectedCliente,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    showInactive,
    setShowInactive,
    loadClientes,
    refreshSelectedCliente,
  };
};

export type { Cliente };
